/**
 * Modulo per la gestione della pagina "Le Tue Vendite" area venditore
 * Gestisce l'eliminazione di annunci e l'invio di richieste di contatto per supporto
 */

import Page from "~/pages/Page";
import { getApi } from "~/auth";
import UI from "~/ui";
import $ from "jquery";
import { generaRecaptcha, recaptchaReady, recaptchaToken } from "~/recaptcha";

/**
 * Interfaccia per la risposta della chiamata API
 */
interface ApiResponse {
    success: boolean;
    responseText?: string;
    message?: string;
}

/**
 * Codici operazione per il tracciamento
 */
const CODICE_OPERAZIONE = {
    DeletePratica: "DELETE_PRATICA"
};

/**
 * Classe per la gestione della pagina Le Tue Vendite
 */
class LeTueVendite extends Page {

    private auth: any = null;
    private userInfo: any = null;
    private isUserAuthenticated: boolean = false;
    private isSubmitting: boolean = false;
    private recaptchaGenerated: boolean = false;
    private currentLottoId: string = "";
    private currentPraticaData: any = null;

    constructor() {
        super("letuevendite", "LeTueVendite");
    }

    /**
     * Inizializza la pagina
     */
    pageStart(): Promise<void> {
        this.auth = getApi();
        this.userInfo = this.auth.getCached("userInfo");
        this.isUserAuthenticated = this.userInfo !== null && this.userInfo !== undefined && this.userInfo.anagraficaId > 0;

        // Inizializza i gestori eventi
        this.initDeleteHandlers();
        this.initContactStaffHandlers();
        this.initModalHandlers();

        return Promise.resolve();
    }

    /**
     * Inizializza i gestori per l'eliminazione degli annunci
     */
    private initDeleteHandlers(): void {
        $("a.elimina").on('click', (e: JQuery.ClickEvent) => {
            e.preventDefault();
            const praticaId = $(e.currentTarget).attr("data-praticaid");

            if (praticaId) {
                UI.confirm(
                    "Conferma Eliminazione",
                    "Sei sicuro di voler eliminare questo annuncio?",
                    undefined,
                    (result: boolean) => {
                        if (result) {
                            this.eliminaAnnuncio(praticaId);
                        }
                    }
                );
            }
        });
    }

    /**
     * Elimina un annuncio/pratica
     */
    private async eliminaAnnuncio(praticaId: string): Promise<void> {

        this.spinner.spin();

        try {

            const url = `${window.BaseUrlWebApi}VenditePrivate/DeletePratica?praticaId=${praticaId}`;
            await this.auth.fetchWithAuth("DELETE " + url, {
                cache: "no-cache",
                headers: { 'content-type': 'application/json' },
            });

            UI.confirmOneButton(
                "",
                "Annuncio eliminato con successo",
                undefined,
                () => {
                    $(`#row-${praticaId}`).remove();
                }
            );

        } catch (error: any) {
            console.error("Errore durante l'eliminazione dell'annuncio:", error);
            UI.confirmOneButton("", "Errore durante l'eliminazione dell'annuncio");
        } finally {
            this.spinner.stop();
        }
    }

    /**
     * Inizializza i gestori per il contatto con lo staff
     */
    private initContactStaffHandlers(): void {
        // Genera il token reCAPTCHA al click sul pulsante contatta staff
        $(".btn-contatta-staff").each((index, element) => {
            $(element).on("click", async (e: JQuery.ClickEvent) => {
                e.preventDefault();

                await this.generateRecaptcha();

                const lottoId = $(e.currentTarget).attr("data-lottoid");
                if (lottoId) {
                    this.currentLottoId = lottoId;
                    const $row = $(`div[data-valid='${lottoId}']`);
                    this.populateModalWithData($row);
                    $('#modal-contatto-le-tue-vendite').modal('show');
                }
            });
        });
    }

    /**
     * Genera il reCAPTCHA
     */
    private async generateRecaptcha(): Promise<void> {
        try {
            await recaptchaReady();
            await generaRecaptcha("letuevendite");
            this.recaptchaGenerated = true;
            console.info('reCAPTCHA generato per il form contatto vendita');
        } catch (error) {
            console.error('Errore durante la generazione del reCAPTCHA:', error);
            this.recaptchaGenerated = false;
        }
    }

    /**
     * Popola il modale con i dati dell'annuncio selezionato
     */
    private populateModalWithData($row: JQuery): void {
        if (this.isUserAuthenticated && this.userInfo) {
            // Precompila i dati dell'utente
            $("#nome-vendita").val(this.userInfo.nome || "");
            $("#cognome-vendita").val(this.userInfo.cognome || "");
            $("#email-vendita").val(this.userInfo.email || "");

            // ✅ Recupera il telefono o cellulare dall'array contatti
            const telefono = this.getTelefonoFromUserInfo();
            if (telefono) {
                $("#telefono-vendita").val(telefono);
            }

            // Popola i dati della pratica
            //const lottoId = $row.find("#lottoId").val();
            const indirizzo = $row.find("#indirizzo").text();
            const tipologia = $row.find("#tipologia").text().replace(/\s/g, '');
            const comune = $row.find("#comune-prov").text();

            $("#hdCodiceId").val(this.currentLottoId);
           // $("#hdCodiceId").val(lottoId?.toString() || "");
            $("#tipologiabene-vendita").val(tipologia);
            $("#indirizzo-vendita").val(indirizzo);
            $("#citta-vendita").val(comune);

            // Salva i dati per riferimento
            this.currentPraticaData = {
                lottoId: this.currentLottoId,
                indirizzo: indirizzo,
                tipologia: tipologia,
                comune: comune
            };
        }
    }

    /**
     * ✅ Recupera il numero di telefono o cellulare dall'array contatti dell'utente
     * Priorità: Cellulare (CEL) > Telefono (TEL)
     */
    private getTelefonoFromUserInfo(): string | null {
        if (!this.userInfo || !this.userInfo.contatti || !Array.isArray(this.userInfo.contatti)) {
            return null;
        }

        // Cerca prima un cellulare (CEL)
        const cellulare = this.userInfo.contatti.find((c: any) =>
            c.tipoContatto === 'CELL' && c.valore
        );

        if (cellulare) {
            return this.formatTelefono(cellulare);
        }

        // Se non c'è cellulare, cerca un telefono (TEL)
        const telefono = this.userInfo.contatti.find((c: any) =>
            c.tipoContatto === 'TEL' && c.valore
        );

        if (telefono) {
            return this.formatTelefono(telefono);
        }

        return null;
    }

    /**
     * ✅ Formatta il numero di telefono con eventuale prefisso
     */
    private formatTelefono(contatto: any): string {
        if (!contatto || !contatto.valore) {
            return '';
        }

        // Se c'è un prefisso, lo concatena
        if (contatto.prefisso && contatto.prefisso.trim() !== '') {
            return `${contatto.prefisso} ${contatto.valore}`;
        }

        return contatto.valore;
    }

    /**
     * Inizializza i gestori del modale
     */
    private initModalHandlers(): void {
        const theForm = document.getElementById('form-contatto-vendita') as HTMLFormElement;

        if (!theForm) {
            console.warn("Form contatto vendita non trovato");
            return;
        }

        // Gestione submit del form
        $("#invia-vendita").on("click", async (e: JQuery.ClickEvent) => {
            e.preventDefault();
            e.stopPropagation();

            // Impedisce il doppio click
            if (this.isSubmitting) {
                console.log("Invio già in corso...");
                return;
            }

            if (theForm.checkValidity()) {
                await this.richiestaContattoAnnuncio(theForm);
            } else {
                theForm.classList.add('was-validated');
            }
        });

        // Reset del form quando si chiude il modale
        $('#modal-contatto-le-tue-vendite').on('hidden.bs.modal', () => {
            theForm.reset();
            theForm.classList.remove('was-validated');
            $(".invalid-feedback").hide();
            this.recaptchaGenerated = false;
        });
    }

    /**
     * Invia la richiesta di contatto per l'annuncio
     */
    private async richiestaContattoAnnuncio(theForm: HTMLFormElement): Promise<void> {
        // Verifica il reCAPTCHA prima di procedere
        if (!this.recaptchaGenerated) {
            UI.confirmOneButton("", "Errore: reCAPTCHA non generato");
            return;
        }

        // Ottieni il token dal campo hidden
        const recaptchaTokenValue = recaptchaToken();

        if (!recaptchaTokenValue) {
            UI.confirmOneButton("", "Completa la verifica reCAPTCHA");
            return;
        }

        // Imposta il flag per impedire doppi click
        this.isSubmitting = true;
        this.disableSubmitButton(true);

        // Serializza i dati del form
        const formData: Record<string, any> = {};
        $(theForm)
            .serializeArray()
            .forEach((field) => {
                formData[field.name] = field.value;
            });

        // Aggiungi il token reCAPTCHA
        formData["RecaptchaToken"] = recaptchaTokenValue;

        console.info("Invio richiesta contatto annuncio:", formData);

        this.spinner.spin();

        try {
            const url = `${window.BaseUrlWeb}Venditore/RichiestaContattoLetueVendite`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json; charset=utf-8'
                },
                /* credentials: 'include',*/
                body: JSON.stringify(formData)
            });

            const data: ApiResponse = await response.json();

            if (data.success) {
                // Mostra messaggio di successo
                UI.confirmOneButton(
                    "Messaggio inviato",
                    "La tua richiesta è stata inviata con successo. Verrai contattato al più presto."
                );

                // Reset del form e chiusura modale
                theForm.reset();
                theForm.classList.remove('was-validated');
                $('#modal-contatto-le-tue-vendite').modal('hide');

                // Rigenera il reCAPTCHA per il prossimo utilizzo
                this.recaptchaGenerated = false;
            }
            else {
                const errorMessage = data.responseText || data.message || "Errore sconosciuto";
                UI.confirmOneButton("", `Errore: Non è stato possibile inviare la E-mail (${errorMessage})`);

                // Rigenera il reCAPTCHA in caso di errore
                await this.generateRecaptcha();
            }

        } catch (error: any) {
            console.error("Errore durante l'invio della richiesta:", error);
            const errorMessage = error.message || "Si è verificato un errore durante l'invio";
            UI.confirmOneButton("", `Errore durante l'invio della richiesta: ${errorMessage}`);

            // Rigenera il reCAPTCHA in caso di errore
            await this.generateRecaptcha();
        } finally {
            this.spinner.stop();
            this.isSubmitting = false;
            this.disableSubmitButton(false);
        }
    }

    /**
     * Disabilita/abilita il pulsante di submit
     */
    private disableSubmitButton(disable: boolean): void {
        const $submitButton = $("#invia-vendita");

        if (disable) {
            $submitButton.prop('disabled', true);
            // Aggiunge uno spinner al pulsante
            const originalText = $submitButton.html();
            $submitButton.data('original-text', originalText);
            $submitButton.html('<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Invio in corso...');
        } else {
            $submitButton.prop('disabled', false);
            // Ripristina il testo originale
            const originalText = $submitButton.data('original-text');
            if (originalText) {
                $submitButton.html(originalText);
            }
        }
    }

}

export default LeTueVendite;
